<template>
  <div>
    <a-table
      :columns="columns"
      :data-source="transactions"
      :loading="loading"
      :pagination="{ pageSize: 10 }"
      :scroll="{ x: 'max-content' }"
      :rowClassName="
        (record) =>
          record.operationType === 'DEBIT' ? 'debit-row' : 'credit-row'
      "
    >
      <template #title>
        <div class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">{{ $t("caisse.historiqueTransactions") }}</h5>
          <a-range-picker 
            v-model="dateRange" 
            @change="handleDateChange"
            :placeholder="[$t('all.startDate'), $t('all.endDate')]"
          />
        </div>
      </template>

      <template slot="date" slot-scope="text, record">
        {{ formatDate(text) }}
      </template>

      <template slot="amount" slot-scope="text, record">
        <span
          :class="
            record.operationType === 'DEBIT' ? 'text-success' : 'text-danger'
          "
        >
          {{ formatAmount(text) }}
        </span>
      </template>

      <template slot="fluxType" slot-scope="text, record">
        <a-tag :color="getTypeColor(text)">{{
          $t(`recette.fluxType.${text}`)
        }}</a-tag>
        <small class="text-muted" v-if="record.type">
          {{ record.type }}
        </small>
      </template>

      <template slot="source" slot-scope="text, record">
        <div>
          <div>{{ record.caisseSource && record.caisseSource.name }}</div>
          <small class="text-muted">{{
            record.caisseSource && record.caisseSource.reference
          }}</small>
        </div>
      </template>

      <template slot="destination" slot-scope="text, record">
        <div>
          <div>
            {{ record.caisseDestination && record.caisseDestination.name }}
          </div>
          <small class="text-muted">{{
            record.caisseDestination && record.caisseDestination.reference
          }}</small>
        </div>
      </template>

      <template slot="user" slot-scope="text, record">
        <div>
          <div>{{ record.doneBy && record.doneBy.userName }}</div>
        </div>
      </template>

      <template slot="actions" slot-scope="text, record">
        <a-button type="primary" size="small" @click="showDetails(record)">
          {{ $t("caisse.details") }}
        </a-button>
      </template>
    </a-table>

    <a-modal
      v-model="modalVisible"
      :title="$t('caisse.detailsTransaction')"
      @cancel="handleModalClose"
      :footer="null"
      width="70%"
    >
      <template v-if="selectedRecord">
        <!-- Transaction Details Section -->
        <a-descriptions bordered size="small" :column="3">
          <a-descriptions-item :label="$t('caisse.dateColonne')">
            {{ formatDate(selectedRecord.createdAt) }}
            <small class="text-muted d-block">{{
              formatDate(selectedRecord.createdAt, true)
            }}</small>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('caisse.utilisateurTransaction')">
            <small
              class="text-muted d-block"
              v-if="selectedRecord.doneBy && selectedRecord.doneBy.userName"
            >
              {{ selectedRecord.doneBy.userName }}
            </small>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('caisse.type')">
            <a-tag :color="getTypeColor(selectedRecord.fluxType)">{{
              selectedRecord.fluxType
            }}</a-tag>
            <small class="text-muted d-block" v-if="selectedRecord.type">{{
              selectedRecord.type
            }}</small>
          </a-descriptions-item>
          <a-descriptions-item
            :label="$t('caisse.montantColonne')"
            :span="selectedRecord.fluxType === 'COLLECTE_SOUS_CAISSES' ? 1 : 2"
          >
            <span
              :class="
                selectedRecord.operationType === 'DEBIT'
                  ? 'text-success'
                  : 'text-danger'
              "
            >
              {{ formatAmount(selectedRecord.amount) }}
            </span>
          </a-descriptions-item>
          <a-descriptions-item
            v-if="selectedRecord.fluxType === 'COLLECTE_SOUS_CAISSES'"
            :label="$t('caisse.typeDeCollecte')"
          >
            <a-tag :color="selectedRecord.collectTotal ? '#52c41a' : '#faad14'">
              {{
                selectedRecord.collectTotal
                  ? $t("caisse.totale")
                  : $t("caisse.partielle")
              }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('caisse.sourceColonne')">
            {{
              selectedRecord.caisseSource && selectedRecord.caisseSource.name
            }}
            <small class="text-muted d-block">
              {{
                selectedRecord.caisseSource &&
                selectedRecord.caisseSource.reference
              }}
            </small>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('caisse.destinationColonne')">
            {{
              selectedRecord.caisseDestination &&
              selectedRecord.caisseDestination.name
            }}
            <small class="text-muted d-block">
              {{
                selectedRecord.caisseDestination &&
                selectedRecord.caisseDestination.reference
              }}
            </small>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('caisse.commentaire')" :span="3">
            {{ selectedRecord.comment || "-" }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- State History Section -->
        <template v-if="selectedRecord.history">
          <a-divider>{{ $t("caisse.etatCaisses") }}</a-divider>
          <a-row :gutter="16">
            <a-col :span="12">
              <CaisseDetails
                v-if="selectedRecord.history.caisseSourceState"
                :stateData="selectedRecord.history.caisseSourceState"
                :title="$t('caisse.etatCaisseSource')"
                :showAuthorizedUsers="false"
              />
            </a-col>
            <a-col :span="12">
              <CaisseDetails
                v-if="selectedRecord.history.caisseDestinationState"
                :stateData="selectedRecord.history.caisseDestinationState"
                :title="$t('caisse.etatCaisseDestination')"
                :showAuthorizedUsers="false"
              />
            </a-col>
          </a-row>
        </template>
      </template>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import CaisseDetails from "./CaisseDetails.vue";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "CaisseHistoryTab",
  components: {
    CaisseDetails,
  },
  props: {
    caisseId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      transactions: [],
      dateRange: [],
      currency: null,
      columns: [
        {
          title: this.$t("caisse.dateColonne"),
          dataIndex: "createdAt",
          key: "date",
          scopedSlots: { customRender: "date" },
          sorter: true,
          width: 150,
        },
        {
          title: this.$t("caisse.typeDeFluxColonne"),
          dataIndex: "fluxType",
          key: "fluxType",
          width: 120,
          scopedSlots: { customRender: "fluxType" },
        },
        {
          title: this.$t("caisse.montantColonne"),
          dataIndex: "amount",
          key: "amount",
          align: "right",
          width: 150,
          scopedSlots: { customRender: "amount" },
        },
        {
          title: this.$t("caisse.sourceColonne"),
          dataIndex: "caisseSource",
          key: "source",
          width: 150,
          scopedSlots: { customRender: "source" },
        },
        {
          title: this.$t("caisse.destinationColonne"),
          dataIndex: "caisseDestination",
          key: "destination",
          width: 150,
          scopedSlots: { customRender: "destination" },
        },
        {
          title: this.$t("caisse.utilisateurColonne"),
          dataIndex: "doneBy",
          key: "user",
          width: 180,
          scopedSlots: { customRender: "user" },
        },
        {
          title: this.$t("caisse.actionsColonne"),
          key: "actions",
          width: 100,
          scopedSlots: { customRender: "actions" },
        },
      ],
      selectedRecord: null,
      modalVisible: false,
    };
  },
  created() {
    this.currency = getBuildingCurrency();
    this.fetchTransactions();
  },
  methods: {
    async fetchTransactions() {
      this.loading = true;
      try {
        const res = await apiClient.get(
          `/compV3/caisse-transfer/${this.caisseId}`
        );
        this.transactions = res.data;
      } catch (error) {
        this.$message.error(this.$t("caisse.erreurChargementTransactions"));
      } finally {
        this.loading = false;
      }
    },
    handleDateChange() {
      this.fetchTransactions();
    },
    formatDate(date, timeOnly = false) {
      if (!date) return "";
      return timeOnly
        ? moment(date).format("HH:mm")
        : moment(date).format("DD/MM/YYYY HH:mm");
    },
    formatAmount(amount) {
      return formatCurrencyUtil(amount, this.currency);
    },
    getTypeColor(type) {
      const colors = {
        ALIMENTATION_P: "blue",
        ALIMENTATION_SOUS_CAISSES: "cyan",
        VERSEMENT: "green",
        COLLECTE_SOUS_CAISSES: "orange",
        CHEQUE_TRAITE_BANQUE: "purple",
        CHEQUE_TRAITE_CASH_REPLACEMENT: "purple",
      };
      return colors[type] || "purple";
    },
    showDetails(record) {
      this.selectedRecord = record;
      this.modalVisible = true;
    },
    handleModalClose() {
      this.selectedRecord = null;
      this.modalVisible = false;
    },
  },
};
</script>

<style>
.debit-row {
  background-color: rgba(82, 196, 26, 0.1) !important;
}

.credit-row {
  background-color: rgba(245, 34, 45, 0.1) !important;
}

.mb-3 {
  margin-bottom: 16px;
}

.mt-3 {
  margin-top: 16px;
}

.mr-2 {
  margin-right: 8px;
}

.ml-2 {
  margin-left: 8px;
}

.text-muted {
  color: rgba(0, 0, 0, 0.45);
}

.collection-info-card {
  border-radius: 8px;
  background: #fafafa;
}

.collection-type-tag {
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 4px;
  width: 100%;
  text-align: center;
}

.user-info {
  display: flex;
  align-items: center;
}

.user-details {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 500;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
}

.user-full-name {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.transaction-date {
  display: flex;
  align-items: center;
}

.date-details {
  display: flex;
  flex-direction: column;
}

.date-main {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
}

.date-time {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.text-success {
  color: #52c41a;
}

.text-danger {
  color: #f5222d;
}

.mt-1 {
  margin-top: 4px;
}

.mb-4 {
  margin-bottom: 24px;
}
</style>
